import BrandingWatermarkOutlinedIcon from "@mui/icons-material/BrandingWatermarkOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import {
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_IMAGES } from "../../../../assets";
import AppLoader from "../../../../components/AppLoader";
import DialogBody from "../../../../components/Dialog/Body";
import DialogActionButtons from "../../../../components/Dialog/Footer";
import DialogHeader from "../../../../components/Dialog/Header";
import Label from "../../../../components/Label";
import TextInput from "../../../../components/TextInputs";
import UploadBox from "../../../../components/UploadBox";
import { setGiftCardCurrentPage } from "../../../../lib/slices/pagination/giftCardPageSlice";
import { baseUrl } from "../../../../services";
import {
  createGiftCard,
  updateGiftCard,
} from "../../../../services/giftCardApiFn";
import { capitalizeText, handleOpenSnackBar } from "../../../../utils";
import { translateText } from "../../../../utils/translationService";
import { addEditGiftCardValidation } from "../../../../utils/Validation";
import { CURRENCY } from "../../../../constants/Variables";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(4, 2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

let paramsData = {};

const initialInputValues = {
  image: null,
  amount: "",
  title_en: "",
  title_ro: "",
  description_en: "",
  description_ro: "",
  order: 0,
};

export const AddEditGiftCardDialog = forwardRef(({}, ref) => {
  // Ref for Upload Image Input
  const openImageFile = React.useRef(null);
  // Dispatch Hook
  const dispatch = useDispatch();
  // Translation Hook
  const { t } = useTranslation(["catalogue", "common"]);
  // Open State
  const [open, setOpen] = useState(false);
  // Loading State
  const [isLoading, setIsLoading] = useState({
    save: false,
    translateToEnglish: false,
    image: false,
  });
  // Input Error State
  const [inputError, setInputError] = useState({});
  // Input Values State
  const [inputValues, setInputValues] = useState(initialInputValues);
  // Image File State
  const [imageFile, setImageFile] = useState(null);
  // Track image change state
  const [isImageChanged, setIsImageChanged] = useState(false);
  // Get Search Keys State
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  // Get Current Language State
  const currentLanguage = useSelector(
    (state) => state.languageReducer.currentLanguage,
  );
  // Get Pagination State
  const pagination = useSelector((state) => state.giftCardPaginationReducer);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      paramsData = params;
      if (params.type === "edit") {
        const data = params.data;
        setInputValues({
          image: data?.image || null,
          amount: data?.amount || "",
          title_en: capitalizeText(data?.title?.en) || "",
          title_ro: capitalizeText(data?.title?.ro) || "",
          description_en: capitalizeText(data?.description?.en) || "",
          description_ro: capitalizeText(data?.description?.ro) || "",
        });
      }
    },

    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues(initialInputValues);
    setInputError({});
    setImageFile(null);
    setIsImageChanged(false);
  };

  const handleUploadImage = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (
      !["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(
        file.type,
      )
    ) {
      handleOpenSnackBar(
        "error",
        t("error.invalidImageFormat", { ns: "common" }),
      );
      event.target.value = null;
      return;
    }

    // Revoke previous blob URL if it exists to avoid memory leaks
    if (inputValues.image?.startsWith("blob:")) {
      URL.revokeObjectURL(inputValues.image);
    }

    const previewUrl = URL.createObjectURL(file);

    setImageFile(file);
    setIsImageChanged(true);
    setInputValues((prev) => ({ ...prev, image: previewUrl }));
    setInputError((prev) => ({ ...prev, image: "" }));
    openImageFile.current.value = null;
  };

  const handleRemoveImage = () => {
    if (inputValues.image?.startsWith("blob:")) {
      URL.revokeObjectURL(inputValues.image);
    }
    setInputValues((prev) => ({ ...prev, image: null }));
    setImageFile(null);
    setIsImageChanged(true);
  };

  const uploadImageIfNeeded = async () => {
    if (!isImageChanged) {
      return inputValues.image;
    }

    if (isImageChanged && !imageFile) {
      return null;
    }

    try {
      setIsLoading((prev) => ({ ...prev, image: true }));
      const formData = new FormData();
      formData.append("image", imageFile);

      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response?.data?.url;
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.imageUploadFailed", { ns: "common" }),
      );
      throw error;
    } finally {
      setIsLoading((prev) => ({ ...prev, image: false }));
    }
  };

  const handleAddEdit = async () => {
    const valid = addEditGiftCardValidation(inputValues, setInputError);

    if (valid) {
      return handleOpenSnackBar(
        "error",
        t("resolveTheIssues", { ns: "common" }),
      );
    }

    try {
      setIsLoading((prev) => ({ ...prev, save: true }));

      const imageUrl = await uploadImageIfNeeded();

      let payload = {
        image: imageUrl,
        amount: inputValues.amount,
        title: {
          en: inputValues.title_en,
          ro: inputValues.title_ro,
        },
        description: {
          en: inputValues.description_en,
          ro: inputValues.description_ro,
        },
      };

      if (paramsData?.type === "add") {
        handleApiCall(createGiftCard, payload, "");
      } else {
        handleApiCall(updateGiftCard, payload, paramsData?.data?._id);
      }
    } catch (error) {
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        if (paramsData?.type === "add") {
          dispatch(setGiftCardCurrentPage(1));
        }
        paramsData?.getData(
          searchKeys.giftCard,
          pagination.page,
          pagination.limit,
        );
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.somethingWentWrong", { ns: "common" }),
      );
    }
  };

  const handleTranslateToEnglish = async (key1, key2) => {
    setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.errorWhileTranslating", { ns: "common" }),
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
    }
  };

  const handleChageInputs = (e) => {
    const { name, value, valueAsNumber, type } = e.target;
    const formatedValue = type === "number" ? valueAsNumber : value;

    if (name === "page") {
      setInputValues((pre) => ({ ...pre, positionType: "" }));
    }

    setInputValues((pre) => ({ ...pre, [name]: formatedValue }));
    setInputError((pre) => ({ ...pre, [name]: "" }));
  };

  return (
    <>
      <AppLoader isVisible={isLoading.save} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        // fullWidth
        // maxWidth="md"
      >
        <DialogHeader
          title={
            paramsData.type === "add" ? t("addGiftCard") : t("editGiftCard")
          }
          secondaryHeading={
            paramsData.type === "add"
              ? t("addGiftCardInstruction")
              : t("editGiftCardInstruction")
          }
          icon={<BrandingWatermarkOutlinedIcon />}
          onClose={handleClose}
        />

        <DialogBody>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <UploadBox
                inputRef={openImageFile}
                label={t("image", { ns: "common" })}
                // note={t("imageNote", { ns: "common" })}
                value={inputValues.image}
                error={inputError.image}
                loading={isLoading.image}
                placeholderImage={APP_IMAGES.uploadImage}
                accept="image/png, image/jpeg ,image/jpg ,image/webp"
                height={{ xs: 200, md: 130 }}
                previewSize={{ xs: 160, md: 180 }}
                onClick={() => openImageFile.current.click()}
                onChange={handleUploadImage}
                onRemove={handleRemoveImage}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t("giftCardAmount")}
                htmlFor={`gift-card-amount`}
              />
              <TextInput
                id={`gift-card-amount`}
                required
                placeholder={t("enterGiftCardAmount")}
                type={"number"}
                name={`amount`}
                value={inputValues.amount}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.amount)}
                helperText={t(inputError.amount)}
                InputEndIcon={CURRENCY}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t("nameInRomanian")}
                htmlFor={`gift-card-name-ro`}
              />
              <TextInput
                id={`gift-card-name-ro`}
                required
                placeholder={t("enterNameInRomanian")}
                name={`title_ro`}
                textLength={150}
                value={inputValues.title_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.title_ro)}
                helperText={t(inputError.title_ro)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.title_ro?.length}/150
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t("nameInEnglish")}
                  htmlFor={`gift-card-organization-name-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("title_ro", "title_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`gift-card-organization-name-en`}
                required
                placeholder={t("enterNameInEnglish")}
                name={`title_en`}
                textLength={150}
                value={inputValues.title_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.title_en)}
                helperText={t(inputError.title_en)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.title_en?.length}/150
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t("descriptionInRomanian")}
                  htmlFor={`gift-card-description-ro`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Typography variant="body2" color="text.grey">
                  {inputValues.description_ro?.length}/300
                </Typography>
              </Stack>
              <TextInput
                id={`gift-card-description-ro`}
                required
                placeholder={t("enterDescriptionInRomanian")}
                name={`description_ro`}
                textLength={300}
                value={inputValues.description_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.description_ro)}
                helperText={t(inputError.description_ro)}
                multiline={true}
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t("descriptionInEnglish")}
                  htmlFor={`gift-card-description-en`}
                  labelProps={{ marginBottom: 0 }}
                />

                <Chip
                  label={
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      gap={1}
                      width={"100%"}
                    >
                      <Typography variant="body2">
                        {t("translateToEnglish", { ns: "common" })}
                      </Typography>
                      <Typography variant="body2" color="text.grey">
                        {inputValues.description_en?.length}/300
                      </Typography>
                    </Stack>
                  }
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("description_ro", "description_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`gift-card-description-en`}
                required
                placeholder={t("enterDescriptionInEnglish")}
                name={`description_en`}
                textLength={300}
                value={inputValues.description_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.description_en)}
                helperText={t(inputError.description_en)}
                multiline={true}
                rows={4}
              />
            </Grid>
          </Grid>
        </DialogBody>
        <DialogActionButtons
          onConfirm={handleAddEdit}
          onCancel={handleClose}
          confirmText={"save"}
          cancelText={"cancel"}
          isConfirmBtnDisable={isLoading.save}
          showCancelButton={true}
        />
      </BootstrapDialog>
    </>
  );
});
